
import { IPurchaseOrderListLine } from '@/entity/purchase-order/purchase-order-list';
import { IStudio } from '@/entity/project/studio';
import { IBootstrapTableColumn } from '@/entity/shared/bootstrap';
import { purchaseOrderApi } from '@/wapi/purchase-order-api';
import { ICancellableResult, isCallValidAndNotCancelled } from '@t/ajax-wrapper';
import { appTokenMgr, IEmployeeAppRoleManager } from '@t/employee-app-role';
import { NU } from '@t/type';
import { format, isAfter, isEqual } from 'date-fns';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import frenchLocale from 'date-fns/locale/fr';
import { vxm } from '@/store';
import { IEntity } from '@/entity/shared/EntityStoreTypes';
import SelectEntity from '@c/shared/select-entity.vue';

@Component({
    components: { SelectEntity }
})
export default class PurchaseOrderBaseList extends Vue {
    private columnPlaceholder: string = 'Nouvelle colonne';
    private purchaseOrders: IPurchaseOrderListLine[] = [];
    private filteredPurchaseOrders: IPurchaseOrderListLine[] = [];
    private promiseExport: boolean = false;
    private isBusy: boolean = false;
    private mySortBy: string = '';
    private mySortDesc: boolean = false;
    private entity: IEntity | null = null;
    public get addedAdditionalFields() {
        return vxm.lists.addedAdditionalFields('purchases-list-state');
    }

    private selectedFieldToAdd: NU<IBootstrapTableColumn> = null;

    private startDate: Date = this.getBaseDate();
    private endDate: Date | null = null;

    get listOfFilters() {
        return vxm.lists.filters('purchases-list-state');
    }

    get appTokenMgr(): IEmployeeAppRoleManager {
        return appTokenMgr;
    }

    get additionalFieldChoice(): Array<IBootstrapTableColumn> {
        return vxm.lists.additionnalFieldChoice('purchases-list-state');
    }

    get fields(): Array<IBootstrapTableColumn | string> {
        return vxm.lists.listFields('purchases-list-state');
    }

    get filteredDataByColumn(): IPurchaseOrderListLine[] {
        const keys = Object.keys(this.listOfFilters);
        const filtersApplied = keys.filter((x) => this.listOfFilters[x].value.length > 0);
        if (filtersApplied.length > 0) {
            this.filteredPurchaseOrders = this.purchaseOrders.filter((p) =>
                filtersApplied.every((f) => this.filterListByColumn(f, p))
            );
            return this.purchaseOrders.filter((p) => filtersApplied.every((f) => this.filterListByColumn(f, p)));
        }
        this.filteredPurchaseOrders = this.purchaseOrders.filter((p) =>
            filtersApplied.every((f) => this.filterListByColumn(f, p))
        );
        return this.purchaseOrders;
    }

    public amountListFormatter(val): string {
        if (val.value) {
            return val.value.slice(0, -1) + val.item.currencySymbol;
        }
        return '';
    }

    public async onEntitySelected(entity : IEntity): Promise<void> {
        if (entity.id) {
            this.entity = entity;
            await this.fetchDataWithDate(entity.id);
        }
    }

    @Watch('selectedFieldToAdd')
    public addColumn(column: string, _oldVal: string): void {
        return vxm.lists.addColumn({ listKey: 'purchases-list-state', column });
    }

    private async fetchDataWithDate(entityId : number) {
        this.isBusy = true;
        const resPurchaseOrders = await purchaseOrderApi.getGlobalPurchaseOrderList(
            new Date(this.startDate),
            this.endDate != null ? new Date(this.endDate) : null,
            entityId
        );

        if (
            isCallValidAndNotCancelled<Array<IPurchaseOrderListLine>>(resPurchaseOrders) &&
            resPurchaseOrders.datas != null
        ) {
            this.purchaseOrders = resPurchaseOrders.datas;
        }
        this.isBusy = false;
    }

    public deleteColumn(column: string): void {
        return vxm.lists.removeColumn({ listKey: 'purchases-list-state', column });
    }

    async mounted() {
        vxm.app.changeTitleMain('Projets');
        vxm.app.changeTitleExt('Liste des bons de commandes');
        vxm.lists.init('purchases-list-state');

        await vxm.entity.fetchEntities();
    }

    private getBaseDate() {
        const current = new Date();

        const d = new Date();
        d.setMonth(9);
        d.setDate(1);
        d.setFullYear(current.getFullYear());

        if (isAfter(d, current)) {
            d.setFullYear(d.getFullYear() - 1);
        }

        /** TEST */
        // d.setFullYear(2021);
        // d.setMonth(2);
        // d.setDate(3);
        // d.setHours(3);
        // d.setMinutes(0);
        // d.setSeconds(0);
        /** /TEST */

        return d;
    }

    private onFiltered(filteredItems: IPurchaseOrderListLine[]) {
        this.filteredPurchaseOrders = filteredItems;
    }

    public filterList(): void {
        const keys = Object.keys(this.listOfFilters);
        const filtersApplied = keys.filter((x) => this.listOfFilters[x].value.length > 0);
        if (filtersApplied.length > 0) {
            this.filteredPurchaseOrders = this.purchaseOrders.filter((p) =>
                filtersApplied.every((f) => this.filterListByColumn(f, p))
            );
            this.purchaseOrders.filter((p) => filtersApplied.every((f) => this.filterListByColumn(f, p)));
        }
        this.filteredPurchaseOrders = this.purchaseOrders;
    }

    async exportPurchaseOrders(): Promise<void> {
        const sortKeySelected = this.mySortBy;
        const sortKeyDescSelected = this.mySortDesc;
        const listIds = this.filteredDataByColumn.map(x => x.id);

        if (this.entity?.id) {
            await this.generateReport(
                purchaseOrderApi.exportPurchaseOrderGlobalListData(
                    listIds!,
                    new Date(this.startDate),
                    this.endDate != null ? new Date(this.endDate) : null,
                    this.addedAdditionalFields.map((x) => x.key!),
                    sortKeySelected,
                    sortKeyDescSelected,
                    this.entity.id
                ),
                `Liste_des_bonsdecommandes_${this.formatDate(new Date())}.xlsx`
            );
        }
    }

    private formatDate(date: Date): string {
        return format(new Date(String(date)), 'yyyy-MM-dd', { locale: frenchLocale }) ?? '';
    }

    private async generateReport(request: Promise<ICancellableResult<string>>, reportName: string): Promise<void> {
        this.promiseExport = true;
        const response = await request;
        if (response && response.datas) {
            const blob = new Blob([response.datas], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;'
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.download = reportName;
            link.click();
            this.promiseExport = false;
        }
        // We wait for the file to download before removing loading state
        setTimeout(() => {
            this.promiseExport = false;
        }, 1000);
    }

    public filterListByColumn(key: string, x: IPurchaseOrderListLine): boolean {
        const containsCheck = [
            'studio',
            'trigram',
            'thirdPartyName',
            'purchaseOrderReference',
            'client',
            'company',
            'name'
        ];
        const dateCheck = ['date', 'dueDate', 'paymentDate'];
        const numberCheck = ['amountExcTax', 'amountIncTax'];

        if (containsCheck.find((x) => x === key)) {
            return x[key]?.toUpperCase().includes(this.listOfFilters[key]?.value.toUpperCase());
        } else if (dateCheck.find((x) => x === key)) {
            return new Date(x[key]).toLocaleDateString('fr-FR').includes(this.listOfFilters[key].value);
        } else if (numberCheck.find((x) => x === key)) {
            return this.listOfFilters[key].value === x[key];
        }
        return false;
    }

    private strcmp(a, b) {
        return a < b ? -1 : a > b ? 1 : 0;
    }

    private myCompare(itemA: any, itemB: any, key: string) {
        let b0, a0;
        let a, b;
        if (key === 'trigram' || key === 'designation') {
            const a = itemA[key];
            const b = itemB[key];
            return this.strcmp(a.toUpperCase(), b.toUpperCase());
        } else if (key === 'clients' || key === 'agencies') {
            a = itemA[key];
            b = itemB[key];
            if (a.length === 0) {
                a0 = '';
            } else if (a[0].label === undefined) {
                a0 = '';
            } else a0 = a[0].label;
            if (b.length === 0) {
                b0 = '';
            } else if (b[0].label === undefined) {
                b0 = '';
            } else b0 = b[0].label;
            return this.strcmp(a0, b0);
            // } else if (key === 'studioManagerId') {
            //     a = itemA[key];
            //     if (this.graphManagers[a] === '') {
            //         const elementA: MgtPerson = this.$refs['studioManager' + a] as MgtPerson;
            //         this.graphManagers[a] =
            //             elementA && elementA.id ? elementA.personDetails?.displayName ?? 'zzzzzzzzzzzz' : '';
            //     }
            //     if (this.graphManagers[a]) {
            //         a0 = this.graphManagers[a].toLowerCase();
            //     }
            //     b = itemB[key];
            //     if (this.graphManagers[b] === '') {
            //         const elementB: MgtPerson = this.$refs['studioManager' + b] as MgtPerson;
            //         this.graphManagers[b] =
            //             elementB && elementB.id ? elementB.personDetails?.displayName ?? 'zzzzzzzzzzzz' : '';
            //     }
            //     if (this.graphManagers[b]) {
            //         b0 = this.graphManagers[b].toLowerCase();
            //     }
            //     if (a0 === undefined) a0 = '';
            //     if (b0 === undefined) b0 = '';
            //     return this.strcmp(a0.toUpperCase(), b0.toUpperCase());
        }
    }
}
